import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colors, spacing, typography, radius, transitions } from '../../tokens';

type InputSize = 'sm' | 'base' | 'lg';
type InputVariant = 'default' | 'filled' | 'borderless';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  variant?: InputVariant;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const sizes = {
  sm: css`
    height: 32px;
    padding: 0 ${spacing[3]};
    font-size: ${typography.fontSizes.sm};
  `,
  base: css`
    height: 40px;
    padding: 0 ${spacing[4]};
    font-size: ${typography.fontSizes.base};
  `,
  lg: css`
    height: 48px;
    padding: 0 ${spacing[5]};
    font-size: ${typography.fontSizes.lg};
  `,
};

const variants = {
  default: css<{ $error?: boolean }>`
    background: ${colors.background.primary};
    border: 1px solid ${props => props.$error ? colors.feedback.error.border : colors.border.primary};
    
    &:hover:not(:disabled) {
      border-color: ${props => props.$error ? colors.feedback.error.border : colors.border.hover};
    }
    
    &:focus {
      border-color: ${props => props.$error ? colors.feedback.error.primary : colors.border.focus};
      box-shadow: 0 0 0 3px ${props => props.$error ? colors.feedback.error.surface : colors.brand.primarySurface};
    }
  `,
  filled: css<{ $error?: boolean }>`
    background: ${colors.background.secondary};
    border: 1px solid transparent;
    
    &:hover:not(:disabled) {
      background: ${colors.background.tertiary};
    }
    
    &:focus {
      background: ${colors.background.primary};
      border-color: ${props => props.$error ? colors.feedback.error.primary : colors.border.focus};
      box-shadow: 0 0 0 3px ${props => props.$error ? colors.feedback.error.surface : colors.brand.primarySurface};
    }
  `,
  borderless: css<{ $error?: boolean }>`
    background: transparent;
    border: 1px solid transparent;
    border-bottom: 1px solid ${props => props.$error ? colors.feedback.error.border : colors.border.primary};
    border-radius: 0;
    
    &:hover:not(:disabled) {
      border-bottom-color: ${props => props.$error ? colors.feedback.error.border : colors.border.hover};
    }
    
    &:focus {
      border-bottom-color: ${props => props.$error ? colors.feedback.error.primary : colors.border.focus};
      box-shadow: none;
    }
  `,
};

const Container = styled.div<{ $fullWidth?: boolean }>`
  display: ${props => props.$fullWidth ? 'block' : 'inline-block'};
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{
  $size: InputSize;
  $variant: InputVariant;
  $error?: boolean;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
}>`
  width: 100%;
  outline: none;
  border-radius: ${props => props.$variant === 'borderless' ? '0' : radius.base};
  font-family: ${typography.fontFamilies.sans};
  transition: ${transitions.input.base};
  
  ${props => sizes[props.$size]}
  ${props => variants[props.$variant]}
  
  ${props => props.$hasLeftIcon && css`
    padding-left: ${props.$size === 'sm' ? spacing[8] : props.$size === 'lg' ? spacing[12] : spacing[10]};
  `}
  
  ${props => props.$hasRightIcon && css`
    padding-right: ${props.$size === 'sm' ? spacing[8] : props.$size === 'lg' ? spacing[12] : spacing[10]};
  `}
  
  &::placeholder {
    color: ${colors.text.tertiary};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: ${colors.background.disabled};
  }
`;

const IconWrapper = styled.div<{ $position: 'left' | 'right'; $size: InputSize }>`
  position: absolute;
  ${props => props.$position}: ${props => props.$size === 'sm' ? spacing[2] : spacing[3]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  pointer-events: none;
`;

const HelperText = styled.div<{ $error?: boolean }>`
  margin-top: ${spacing[1]};
  font-size: ${typography.fontSizes.xs};
  color: ${props => props.$error ? colors.feedback.error.text : colors.text.secondary};
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  size = 'base',
  variant = 'default',
  error = false,
  errorMessage,
  label,
  helperText,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  ...props
}, ref) => {
  const displayHelperText = error && errorMessage ? errorMessage : helperText;

  return (
    <Container $fullWidth={fullWidth} className={className}>
      {label && <Label>{label}</Label>}
      <InputWrapper>
        {leftIcon && (
          <IconWrapper $position="left" $size={size}>
            {leftIcon}
          </IconWrapper>
        )}
        <StyledInput
          ref={ref}
          $size={size}
          $variant={variant}
          $error={error}
          $hasLeftIcon={!!leftIcon}
          $hasRightIcon={!!rightIcon}
          {...props}
        />
        {rightIcon && (
          <IconWrapper $position="right" $size={size}>
            {rightIcon}
          </IconWrapper>
        )}
      </InputWrapper>
      {displayHelperText && (
        <HelperText $error={error}>{displayHelperText}</HelperText>
      )}
    </Container>
  );
});

Input.displayName = 'Input'; 